import React from 'react';
/* eslint-disable-next-line import/no-extraneous-dependencies */
// Note: lucide-react has been installed via npm install lucide-react
import {
  // Search and UI icons
  Search, X,
  // CRM related icons
  Users, User, UserPlus, Building, Phone, Calendar, Bell, BellRing,
  // Document icons
  FileText, Files, ClipboardList, ClipboardCheck, FileSpreadsheet, 
  // Business icons
  Briefcase, BarChart, PieChart, TrendingUp, DollarSign, CreditCard, Wallet,
  // Communication icons
  MessageSquare, MessageCircle, Mail, Send, Share, 
  // Settings and tools
  Settings, Sliders, Filter, 
  // Status icons
  CheckCircle, XCircle, AlertCircle, Clock, 
  // Other useful icons
  Star, Heart, Bookmark, Tag, Link, Globe, Image
} from 'lucide-react';

type ViewIconDisplayProps = {
  iconType: 'built_in' | 'custom';
  iconKey?: string;
  // iconAssetId is reserved for future use with custom uploaded icons
  // Removing the unused prop to fix the lint error
  className?: string;
  size?: number;
};

/**
 * Component to display view icons in the sidebar and other places
 * Handles both emoji and icon types from the built-in library
 */
const ViewIconDisplay: React.FC<ViewIconDisplayProps> = ({
  iconType,
  iconKey,
  className = '',
  size = 20
}) => {
  // Map of available icons - must match all icons in IconPicker component
  const iconMap: Record<string, any> = {
    // Search and UI icons
    Search,
    X,
    // CRM related icons
    Users,
    User,
    UserPlus,
    Building,
    Phone,
    Calendar,
    Bell,
    BellRing,
    // Document icons
    FileText,
    Files,
    ClipboardList,
    ClipboardCheck,
    FileSpreadsheet,
    // Business icons
    Briefcase,
    BarChart,
    PieChart,
    TrendingUp,
    DollarSign,
    CreditCard,
    Wallet,
    // Communication icons
    MessageSquare,
    MessageCircle,
    Mail,
    Send,
    Share,
    // Settings and tools
    Settings,
    Sliders,
    Filter,
    // Status icons
    CheckCircle,
    XCircle,
    AlertCircle,
    Clock,
    // Other useful icons
    Star,
    Heart,
    Bookmark,
    Tag,
    Link,
    Globe,
    Image
  };

  // Check if the iconKey is an emoji (typically Unicode characters)
  const isEmoji = (text?: string) => {
    if (!text) return false;
    // Simple check: emojis are typically outside the ASCII range
    // This will detect actual emoji characters
    return Array.from(text).some(char => {
      const codePoint = char.codePointAt(0);
      return codePoint && codePoint > 127;
    });
  };

  if (iconType === 'built_in' && iconKey) {
    // If it's an emoji, render it directly
    if (isEmoji(iconKey)) {
      return (
        <span 
          className={`flex items-center justify-center ${className}`}
          style={{ fontSize: Math.max(size * 0.8, 16) }}
        >
          {iconKey}
        </span>
      );
    }
    
    // Handle case where the iconKey might be a JSON string
    try {
      if (typeof iconKey === 'string' && iconKey.startsWith('{')) {
        const parsedIcon = JSON.parse(iconKey);
        if (parsedIcon.type === 'emoji' && parsedIcon.value) {
          return (
            <span 
              className={`flex items-center justify-center ${className}`}
              style={{ fontSize: Math.max(size * 0.8, 16) }}
            >
              {parsedIcon.value}
            </span>
          );
        }
      }
    } catch (e) {
      // Silently fail and continue with normal icon rendering
    }
    
    // If it's a named icon, render the corresponding component
    const IconComponent = iconMap[iconKey];
    if (IconComponent) {
      return <IconComponent className={className} size={size} />;
    }
  }
  
  // For custom icons (future implementation) or fallback
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Building size={size} className="text-gray-400" />
    </div>
  );
};

export default ViewIconDisplay;
